import Modal from 'react-modal';
import "../../styles/AdminPage.css";
import Button, { BUTTON_TYPES } from '../Button';
import TextInput from '../inputs/TextInput';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import DateInput from '../inputs/DateInput';
import UploadCsvModal from './UploadCsvModal';
import Dropdown from '../inputs/Dropdown';
import { FaTimes } from "react-icons/fa";
import CheckboxGroupScrollable from '../inputs/CheckboxGroupScrollable';
import { deleteEventRecurrence, modifyEvent, modifyEventAndFollowing, modifyEventUpdatedRecurrence } from '../../api/admin/AdminDashboardAPI';
import _ from 'lodash';
import { DateTimeFormatter, LocalDateTime } from '@js-joda/core';
import toast from 'react-hot-toast';
import { useAdmin } from '../../pages/admin/AdminContext';
import RecurrenceSelector from '../inputs/RecurrenceSelector';
import { generateRecurrenceSchedule } from '../../api/admin/RecurrenceFormatter';
import LabeledCheckbox from '../inputs/LabeledCheckBox';

Modal.setAppElement('#root');

const EventEditModal = ({ isOpen, onClose, event, eventId, initialFormData, onFormDataChange, updateEvent, eventTypes, updateEventWithRecurrence }) => {
    const [csvModalOpen, setCsvModalOpen] = useState(false);
    const [csvData, setCsvData] = useState([]);

    const initialRecurrence = event?.recurrence || null;
    const [recurrHasChanged, setRecurrHasChanged] = useState(false);
    const [applyToFollowing, setApplyToFollowing] = useState(false);
    const [deleteFollowing, setDeleteFollowing] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [csvWasProcessed, setCsvWasProcessed] = useState(false);

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("/");
        if (parts.length !== 3) {
            // console.error("Unexpected date format: ", dateStr);
            return "";
        }
        const [month, day, year] = parts;
        if (!month || !day || !year) {
            console.error("Invalid date parts from: ", dateStr);
            return "";
        }
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    };

    const [name, setName] = useState(initialFormData?.name || event?.eventName || "");
    const [startDate, setStartDate] = useState(
        initialFormData?.startDate || event.startDate || ""
    );
    const [endDate, setEndDate] = useState(
        initialFormData?.endDate || event.endDate || ""
    );
    const [selectedRecurrence, setSelectedRecurrence] = useState(initialFormData?.selectedRecurrence || event?.recurrence || "None");
    const [location, setLocation] = useState(initialFormData?.location || event?.location || "");
    const [selectedCommittee, setSelectedCommittee] = useState(initialFormData?.selectedCommittee || (event?.committee && event?.committee.committeeName) || "None");
    const [selectedEventTypeIDs, setSelectedEventTypeIDs] = useState(() =>
        initialFormData?.selectedEventTypeIDs
            ? initialFormData.selectedEventTypeIDs.map(id => Number(id))
            : []
    );

    const [recurrOption, setRecurrOption] = useState(event?.recurrence || null);


    const handleRecurrChange = (change) => {
        const keysToIgnore = ['id', 'startDate'];
        const cleanedInitial = _.omit(initialRecurrence, keysToIgnore);
        const cleanedChange = _.omit(change, keysToIgnore);

        const equal = _.isEqual(cleanedInitial, cleanedChange);

        if (equal) {
            setRecurrHasChanged(false);
        } else {
            setRecurrHasChanged(true);
            setRecurrOption(change);
        }
    };


    // Methods to set state and check for changes
    const handleNameChange = (newName) => {
        setName(newName);
        setIsModified(!_.isEqual(newName, event.eventName));
    };

    const handleLocationChange = (newLocation) => {
        setLocation(newLocation);
        setIsModified(!_.isEqual(newLocation, event.location));
    };

    const handleCommitteeChange = (e) => {
        setSelectedCommittee(e.target.value);
        const eventCommitteeName = event?.committee?.committeeName || "None";
        setIsModified(!_.isEqual(newCommittee, eventCommitteeName));
    };

    const handleEventTypeSelectOnChange = (evt) => {
        const changedValue = Number(evt.target.value);
        const isChecked = evt.target.checked;
        let updatedEventTypeIDs;
        if (isChecked) {
            updatedEventTypeIDs = [...selectedEventTypeIDs, changedValue];
            setSelectedEventTypeIDs(updatedEventTypeIDs); // set state
        } else {
            updatedEventTypeIDs = selectedEventTypeIDs.filter((id) => id !== changedValue);
            setSelectedEventTypeIDs(updatedEventTypeIDs);
        }

        const eventTypeIds = event?.eventTypes?.map(et => et.id) || [];
        setIsModified(!_.isEqual(updatedEventTypeIDs, eventTypeIds));
    };

    const handleEventTypeSelectButton = (newEventIdList) => {
        setSelectedEventTypeIDs(newEventIdList);

        const eventTypeIds = event?.eventTypes?.map(et => et.id) || [];
        setIsModified(!_.isEqual(newEventIdList, eventTypeIds));
    };


    // Open CSV Upload Modal
    const openCsvModal = () => setCsvModalOpen(true);
    const closeCsvModal = () => {
        if (csvWasProcessed) {
            updateEventWithRecurrence();
        }

        setCsvModalOpen(false);
    }

    // Handle CSV data received from UploadCsvModal
    const handleCsvProcessed = (data) => {
        setCsvData(data);
        console.log("Final Processed CSV Data:", data);
        setCsvWasProcessed(true);
    };

    const didInit = useRef(false);
    const onFormDataChangeRef = useRef(onFormDataChange);
    useEffect(() => {
        onFormDataChangeRef.current = onFormDataChange;
    }, [onFormDataChange]);

    useEffect(() => {
        if (isOpen) {
            if (!didInit.current) {
                if (event && (!initialFormData || Object.keys(initialFormData).length === 0)) {
                    setName(event.eventName || "");
                    setStartDate(formatDateForInput(event.startDate || ""));
                    setEndDate(formatDateForInput(event.endDate || ""));
                    setSelectedRecurrence(event?.recurrence || "None");
                    setLocation(event.location || "");
                    setSelectedCommittee((event?.committee && event.committee.committeeName) || "None");

                    if (event.eventTypes) {
                        if (Array.isArray(event.eventTypes)) {
                            const matchedIDs = event.eventTypes.map(et => et.id);
                            setSelectedEventTypeIDs(matchedIDs);
                        } else {
                            const typesFromEvent = event.eventTypes.split(/,\s*/).map(s => s.trim());
                            const matchedIDs = eventTypes
                                .filter(dt => typesFromEvent.includes(dt.eventType))
                                .map(dt => dt.id);
                            setSelectedEventTypeIDs(matchedIDs);
                        }
                    }
                    handleReset();
                }
                didInit.current = true;
            }
        } else {
            didInit.current = false;
        }
    }, [isOpen, event, initialFormData, eventTypes]);

    useEffect(() => {
        requestAnimationFrame(() => {
            const checkboxes = document.querySelectorAll('input[name="eventTypesGroup"]');
            checkboxes.forEach((checkbox) => {
                const value = Number(checkbox.value);
                checkbox.checked = selectedEventTypeIDs.includes(value);
            });
        });
    }, [selectedEventTypeIDs]);



    const { committees } = useAdmin();

    const committeeOptions = React.useMemo(() => [
        { value: 'None', label: 'None' },
        ...committees.map((committee) => ({
            value: committee.committeeName,
            label: committee.committeeName
        }))
    ], [committees]);

    const recurrenceOptions = [
        { value: 'None', label: 'No Recurrence' },
        { value: 'DAILY', label: 'Daily' },
        { value: 'WEEKLY', label: 'Weekly' },
        { value: 'MONTHLY', label: 'Monthly' },
    ];
    const handleRecurrenceChange = (e) => {
        setSelectedRecurrence(e);
    };

    const handleReset = () => {
        if (event) {
            setName(event.eventName || "");
            setStartDate(event.startDate || "");
            setEndDate(event.endDate || "");
            setSelectedRecurrence(event?.recurrence?.frequency || "None");
            setLocation(event.location || "");
            setSelectedCommittee(event.committee.committeeName || "None");

            if (event.eventTypes) {
                if (Array.isArray(event.eventTypes)) {
                    const matchedIDs = event.eventTypes.map(et => et.id);
                    setSelectedEventTypeIDs(matchedIDs);
                } else if (typeof event.eventTypes === 'string') {
                    const typesFromEvent = event.eventTypes.split(/,\s*/).map(s => s.trim());
                    const matchedIDs = eventTypes
                        .filter(dt => typesFromEvent.includes(dt.eventType))
                        .map(dt => dt.id);
                    setSelectedEventTypeIDs(matchedIDs);
                } else {
                    setSelectedEventTypeIDs([]);
                }
            } else {
                setSelectedEventTypeIDs([]);
            }
            setIsModified(false);
        }
    };

    const newCommittee = committees.find(c => c.committeeName === selectedCommittee) || { committeeName: selectedCommittee };
    const newEventTypes = eventTypes.filter(et => selectedEventTypeIDs.includes(et.id));

    // TODO: send to backend with new info, this object may need modifying
    const updatedEvent = {
        id: event.id,
        eventName: name,
        startDate: startDate,
        endDate: endDate,
        location: location,
        committee: { id: newCommittee.id, committeeName: newCommittee.committeeName },
        eventTypes: newEventTypes
    };

    const submitEvent = async () => {
        if (!validateChange()) return;

        if (recurrHasChanged) {
            if (recurrOption === null) {
                // got rid of event recurrence
                handleDeleteRecurrence();
            } else {
                // updating all following recurrences
                handleUpdateRecurrence();
            }

        } else {
            // event is modified but recurrence has not changed
            if (applyToFollowing) {
                handleModifyEventAndFollowing();
            } else {
                // only modified to this event
                handleModifyEvent();
            }
        }
    };


    const validateChange = () => {
        if (recurrOption !== null) {
            recurrOption.startDate = startDate;
        }

        const isUnchanged = _.isEqual(event, updatedEvent);

        if (isUnchanged) {
            toast.error("No data changed");
            return false;
        }

        const isCorrectDate = LocalDateTime.parse(updatedEvent.startDate, DateTimeFormatter.ISO_DATE_TIME)
            .isBefore(LocalDateTime.parse(updatedEvent.endDate, DateTimeFormatter.ISO_DATE_TIME));

        if (!isCorrectDate) {
            toast.error("Start Date & Time must be\n before End Date & Time.");
            return false;
        }

        if (updatedEvent.eventName.length > 80) {
            toast.error("Event Name is too long");
            return false;
        }

        if (selectedRecurrence !== "None") {
            if (!recurrOption?.endDate) {
                toast.error("Please select an end date for the recurrence.");
                return;
            }
            const eventEndDay = updatedEvent.endDate.slice(0, 10);
            if (recurrOption.endDate <= eventEndDay) {
                toast.error("Recurrence end date cannot be before the event end date.");
                return;
            }
            if (selectedRecurrence === "WEEKLY" &&
                (!Array.isArray(recurrOption.weekDays) || !recurrOption.weekDays.length)) {
                toast.error("Select at least one weekday for a weekly recurrence.");
                return;
            }
            if (selectedRecurrence === "MONTHLY" &&
                !Number.isInteger(recurrOption.dateOfMonth)) {
                toast.error("Please pick a day of the month for a monthly recurrence.");
                return;
            }
        }

        return true;
    }

    const handleDeleteRecurrence = () => {
        try {
            toast.loading("Updating Event and Deleting Following...");
            setTimeout(async () => {
                const response = await deleteEventRecurrence(updatedEvent, deleteFollowing);
                if (response.ok) {
                    toast.dismiss();
                    didInit.current = false;
                    updateEventWithRecurrence();
                    toast.success("Events Updated!");
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error updating event. Status:", response.status);
                    toast.error("Events failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    }

    const handleUpdateRecurrence = () => {
        recurrOption.startDate = startDate;
        const recurrDates = generateRecurrenceSchedule(recurrOption).occurrences;
        const jsonBody = {
            event: updatedEvent,
            recurrence: recurrOption,
            dates: recurrDates
        };

        try {
            toast.loading("Updating Event, Updating Recurrence...");
            setTimeout(async () => {
                const response = await modifyEventUpdatedRecurrence(jsonBody);
                if (response.ok) {
                    toast.dismiss();
                    didInit.current = false;
                    updateEventWithRecurrence();
                    toast.success("Events updated!");
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error updating event. Status:", response.status);
                    toast.error("Events failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    }

    const handleModifyEvent = () => {
        try {
            toast.loading("Updating Event...");
            setTimeout(async () => {
                console.log("Payload to update:", updatedEvent);
                const response = await modifyEvent(updatedEvent);
                if (response.ok) {
                    toast.dismiss();
                    didInit.current = false;
                    updateEventWithRecurrence();
                    toast.success("Event updated!");
                    onClose();
                } else {
                    toast.dismiss();
                    console.log("Error updating event. Status:", response.status);
                    toast.error("Event failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    }

    const handleModifyEventAndFollowing = () => {
        updatedEvent.recurrence = event.recurrence
        try {
            toast.loading("Updating Event and Following Events...");
            setTimeout(async () => {
                console.log("Payload to update:", updatedEvent);
                const response = await modifyEventAndFollowing(updatedEvent);
                if (response.ok) {
                    toast.dismiss();
                    didInit.current = false;
                    updateEventWithRecurrence();
                    toast.success("Event updated!");
                    onClose();
                    return;
                } else {
                    toast.dismiss();
                    console.log("Error updating event. Status:", response.status);
                    toast.error("Event failed to update. Status: " + response.status);
                }
            }, 300);
        } catch (error) {
            toast.dismiss();
            console.error("Error submitting event:", error);
            toast.error("Something went wrong. Please check your connection and try again.");
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Event Edit Modal"
            className="custom-modal-content member-filter-modal"
            overlayClassName="custom-modal-overlay"
        >
            <>
                <div className='d-flex flex-row justify-content-between align-items-center mb-3'>
                    <h2 className="text-start mb-0">Edit Event</h2>
                    <div className='d-flex flex-row justify-content-end align-items-center me-1'>
                        <div className='me-3'>
                            <Button
                                id="upload-csv-button"
                                type={BUTTON_TYPES.primary}
                                buttonText="Upload CSV"
                                action={() => openCsvModal()}
                            />
                        </div>
                        <UploadCsvModal
                            isOpen={csvModalOpen}
                            onClose={closeCsvModal}
                            onCsvProcessed={handleCsvProcessed}
                            eventId={eventId}  // Pass the event ID here
                        />
                        <button className="close-btn" onClick={onClose}>
                            <FaTimes />
                        </button>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <TextInput
                            id="eventNameEdit"
                            placeholder="Event Name"
                            className="form-input"
                            charLimit={200}
                            label="Event Name"
                            value={name}
                            setValue={(e) => handleNameChange(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <TextInput
                            id="eventLocationEdit"
                            placeholder="Location"
                            className="form-input"
                            charLimit={300}
                            label="Location"
                            value={location}
                            setValue={(e) => handleLocationChange(e.target.value)}
                            required
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventStartDateEdit"
                            className="form-input"
                            label="Start Date & TIme"
                            value={startDate}
                            setValue={(e) => setStartDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="committee-dropdown"
                            name="committees"
                            options={committeeOptions}
                            onChange={handleCommitteeChange}
                            className="form-input"
                            label="Committee"
                            value={selectedCommittee}
                            required
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <DateInput
                            id="eventEndDateEdit"
                            className="form-input"
                            label="End Date & Time"
                            value={endDate}
                            setValue={(e) => setEndDate(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-field w-50">
                        <Dropdown
                            id="recurrence-dropdown"
                            name="recurrence"
                            options={recurrenceOptions}
                            onChange={(e) => handleRecurrenceChange(e.target.value)}
                            className="form-input"
                            label="Recurrence"
                            value={selectedRecurrence}
                        />
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between mb-1">
                    <div className="form-field w-50 me-2">
                        <CheckboxGroupScrollable
                            id="event-types"
                            name="eventTypesGroup"
                            label="Event Type(s)"
                            options={eventTypes.map((et) => ({
                                value: et.id,
                                label: et.eventType,
                            }))}
                            taller={true}
                            medium={false}
                            selectedValues={selectedEventTypeIDs}
                            onChange={handleEventTypeSelectOnChange}
                            onSelectAll={() => handleEventTypeSelectButton(eventTypes.map((et) => et.id))}
                            onUnselectAll={() => handleEventTypeSelectButton([])}
                            required
                        />
                        {(isModified) && (initialRecurrence !== null) && (!recurrHasChanged) && (
                            <LabeledCheckbox
                                id="edit-following-recurrence"
                                label="Edit all following recurrences?"
                                checked={null}
                                onChange={(c) => setApplyToFollowing(c.target.checked)}
                                disabled={false}
                                colorOptional={"red"}
                            />
                        )}
                    </div>

                    <div className="form-field w-50">
                        <RecurrenceSelector
                            selectedRecurrence={selectedRecurrence}
                            initialValue={recurrOption}
                            onChange={(c) => { handleRecurrChange(c) }}
                        />
                        {(recurrHasChanged) && (initialRecurrence !== null) && (recurrOption === null) && (
                            <LabeledCheckbox
                                id="following-recurr-checkbox"
                                label="Delete Following Recurrences?"
                                checked={null}
                                onChange={(c) => setDeleteFollowing(c.target.checked)}
                                disabled={false}
                                optionalMessage="<b>Warning:</b> <br> You are about to remove this recurrence relationship"
                                colorOptional={"red"}
                            />
                        )}
                    </div>
                </div>
                <div className='d-flex flex-row justify-content-center align-items-center two-button-container'>
                    <div className='me-3'>
                        <Button
                            id="reset-button"
                            type={BUTTON_TYPES.outline}
                            buttonText="Reset"
                            action={handleReset}
                        />
                    </div>
                    <Button
                        id="filter-button-modal"
                        type={BUTTON_TYPES.primary}
                        buttonText="Save"
                        action={submitEvent}
                    />
                </div>
            </>
        </Modal>
    );
};

EventEditModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    event: PropTypes.object,
    initialFormData: PropTypes.object,
    onFormDataChange: PropTypes.func,
};

export default EventEditModal;
